import {
  getRequestsRailwayEnum,
  type GetRequestsRailwayEnumKey,
  type GetRequestsMonitoringMetricKey,
} from "@synthesis/shared-services/internal/application/types";

import type { MonitoringResponse, MonitoringRequestItem } from "@synthesis/shared-services/internal/monitoring/types";

export const ALL_RAILWAYS_VALUE = "ALL";

const normalize = (value: string) =>
  value
    .toLocaleLowerCase("ru-RU")
    .replaceAll("ё", "е")
    .replace(/[^a-zа-я0-9]+/g, " ")
    .trim();

export const resolveMonitoringRailway = (candidates: string[]): RailwayShareItem | undefined => {
  const normalized = candidates.map(normalize);
  const matches = RAILWAY_SHARES.filter((railway) =>
    normalized.some((candidate) => candidate === normalize(railway.id) || ` ${candidate} `.includes(` ${normalize(railway.name)} `)),
  );
  return matches.length === 1 ? matches[0] : undefined;
};

export type RailwayScope = "active" | "all";

export type MonitoringMetricTone = "turquoise" | "blue" | "success" | "error" | "violet" | "olive" | "orange" | "yellow";

export type MonitoringMetricInteraction = "registry" | "popover";

export type MonitoringDeadlineState = "PLANNED" | "DEADLINE_APPROACHING" | "RISK_OF_VIOLATION" | "OVERDUE";

export type MonitoringMetricItem = {
  railwayId?: string;
  id: GetRequestsMonitoringMetricKey;
  requestCount?: number;
  value: number;
  label: string;
  percent: number;
  hint?: string;
  tone: MonitoringMetricTone;
  icon:
    | "file-fill"
    | "calendar-fill"
    | "checkbox-circle-fill"
    | "close-circle-fill"
    | "rub-round"
    | "rub-round-forbidden"
    | "rub-round-not-defined"
    | "payment-add"
    | "payment-check"
    | "payment-error"
    | "time-fill"
    | "time-warning-fill"
    | "time-end-fill"
    | "file-list-add-fill"
    | "file-list-time-fill"
    | "file-list-check-fill";
  tooltip: string;
  interaction: MonitoringMetricInteraction;
};

export type RailwayShareItem = {
  id: GetRequestsRailwayEnumKey;
  name: string;
  count: number;
  percent: number;
};

export type MonitoringPopoverApplication = {
  id: string;
  number: number;
  title: string;
  railwayName: string;
  remainingDays?: number;
  deadlineState?: MonitoringDeadlineState;
  amount?: number;
};

export type MonitoringSectionTooltip = {
  title: string;
  description: string;
};

export const METRIC_TONE_ICON_CLASS: Record<MonitoringMetricTone, string> = {
  turquoise: "text-additional-turquoise-50",
  blue: "text-additional-blue-50",
  success: "text-system-success-50",
  error: "text-system-error-50",
  violet: "text-additional-violet-50",
  olive: "text-additional-olive-50",
  orange: "text-additional-orange-50",
  yellow: "text-additional-yellow-50",
};

export const MONITORING_SECTION_TOOLTIPS = {
  system: {
    title: "Показывает текущее состояние системы: общий объём заявок и основные финансовые показатели.",
    description:
      "Наведите курсор на показатель, чтобы узнать, что он означает.\nНажмите на показатель, чтобы перейти в реестр с соответствующим фильтром.",
  },
  active: {
    title: "Показывает состояние активных заявок и помогает определить, какие из них требуют внимания.",
    description:
      "Наведите курсор на показатель, чтобы узнать подробности.\nНажмите на показатель, чтобы посмотреть связанные заявки или перейти в реестр с соответствующим фильтром.",
  },
  railways: {
    title:
      "Показывает распределение заявок по дорогам и позволяет оценить нагрузку на каждую из них по активным заявкам или по всем заявкам в системе.",
    description: "Нажмите дорогу, чтобы перейти в реестр с соответствующим фильтром.",
  },
} as const satisfies Record<string, MonitoringSectionTooltip>;

export const SYSTEM_PRIMARY_METRICS: MonitoringMetricItem[] = [
  {
    id: "TOTAL",
    value: 0,
    label: "Всего заявок",
    percent: 0,
    tone: "turquoise",
    icon: "file-fill",
    tooltip: "Все заявки, зарегистрированные в системе, независимо от статуса",
    interaction: "registry",
  },
  {
    id: "ACTIVE",
    value: 0,
    label: "Активные",
    percent: 0,
    tone: "blue",
    icon: "calendar-fill",
    tooltip: "Заявки, которые сейчас на рассмотрении",
    interaction: "registry",
  },
  {
    id: "TU_ISSUED",
    value: 0,
    label: "Выдано ТУ",
    percent: 0,
    tone: "success",
    icon: "checkbox-circle-fill",
    tooltip: "Заявки со статусом «Выдано ТУ»",
    interaction: "registry",
  },
  {
    id: "REJECTED",
    value: 0,
    label: "Мотивированный отказ",
    percent: 0,
    tone: "error",
    icon: "close-circle-fill",
    tooltip: "Заявки со статусом «Мотивированный отказ»",
    interaction: "registry",
  },
];

export const SYSTEM_PAYMENT_METRICS: MonitoringMetricItem[] = [
  {
    id: "PAID_SERVICE",
    value: 0,
    label: "Платная услуга",
    percent: 0,
    tone: "violet",
    icon: "rub-round",
    tooltip: "Заявки, по которым требуется оплата",
    interaction: "registry",
  },
  {
    id: "FREE_SERVICE",
    value: 0,
    label: "Без оплаты",
    percent: 0,
    tone: "turquoise",
    icon: "rub-round-forbidden",
    tooltip: "Заявки, которые не требуют оплаты",
    interaction: "registry",
  },
];

export const SYSTEM_FINANCE_METRICS: MonitoringMetricItem[] = [
  {
    id: "INVOICE_TOTAL",
    value: 0,
    label: "Общая сумма счетов, ₽",
    percent: 0,
    tone: "violet",
    icon: "payment-add",
    tooltip: "Сумма всех выставленных счетов по системе",
    interaction: "registry",
  },
  {
    id: "PAYMENT_TOTAL",
    value: 0,
    label: "Поступило оплат, ₽",
    percent: 0,
    tone: "success",
    icon: "payment-check",
    tooltip: "Сумма всех поступивших оплат",
    interaction: "registry",
  },
  {
    id: "DEBT_TOTAL",
    value: 0,
    label: "Остался долг, ₽",
    percent: 0,
    tone: "error",
    icon: "payment-error",
    tooltip: "Разница между суммой счетов и оплат",
    interaction: "registry",
  },
];

export const ACTIVE_DEADLINE_METRICS: MonitoringMetricItem[] = [
  {
    id: "ACTIVE_PLANNED",
    value: 0,
    label: "Плановый срок",
    percent: 0,
    hint: "более 10 дн.",
    tone: "turquoise",
    icon: "time-fill",
    tooltip: "До окончания срока осталось более 10 рабочих дней",
    interaction: "popover",
  },
  {
    id: "ACTIVE_APPROACHING",
    value: 0,
    label: "Приближается срок",
    percent: 0,
    hint: "6 - 10 дн.",
    tone: "blue",
    icon: "time-fill",
    tooltip: "До окончания срока осталось 6–10 рабочих дней",
    interaction: "popover",
  },
  {
    id: "ACTIVE_AT_RISK",
    value: 0,
    label: "Риск нарушения",
    percent: 0,
    hint: "1 - 5 дн.",
    tone: "yellow",
    icon: "time-warning-fill",
    tooltip: "До окончания срока осталось 1–5 рабочих дней",
    interaction: "popover",
  },
  {
    id: "ACTIVE_OVERDUE",
    value: 0,
    label: "Просрочено",
    percent: 0,
    hint: "0 дн.",
    tone: "error",
    icon: "time-end-fill",
    tooltip: "Срок рассмотрения уже нарушен",
    interaction: "popover",
  },
];

export const ACTIVE_PAYMENT_METRICS: MonitoringMetricItem[] = [
  {
    id: "ACTIVE_UNDEFINED_PAYMENT",
    value: 0,
    label: "Неопределено",
    percent: 0,
    tone: "orange",
    icon: "rub-round-not-defined",
    tooltip: "Решение об оплате ещё не принято",
    interaction: "popover",
  },
  {
    id: "ACTIVE_PAID_SERVICE",
    value: 0,
    label: "Платная услуга",
    percent: 0,
    tone: "violet",
    icon: "rub-round",
    tooltip: "Заявки, по которым требуется оплата",
    interaction: "popover",
  },
  {
    id: "ACTIVE_FREE_SERVICE",
    value: 0,
    label: "Без оплаты",
    percent: 0,
    tone: "turquoise",
    icon: "rub-round-forbidden",
    tooltip: "Заявки, которые не требуют оплаты",
    interaction: "popover",
  },
];

export const ACTIVE_INVOICE_METRICS: MonitoringMetricItem[] = [
  {
    id: "ACTIVE_AWAITING_INVOICE",
    value: 0,
    label: "Ожидает формирования счёта",
    percent: 0,
    tone: "blue",
    icon: "file-list-add-fill",
    tooltip: "Счёт ещё не выставлен",
    interaction: "popover",
  },
  {
    id: "ACTIVE_AWAITING_PAYMENT",
    value: 0,
    label: "Ожидают оплаты",
    percent: 0,
    tone: "orange",
    icon: "file-list-time-fill",
    tooltip: "Счёт выставлен, оплата не поступила",
    interaction: "popover",
  },
  {
    id: "ACTIVE_PAID",
    value: 0,
    label: "Оплачено",
    percent: 0,
    tone: "success",
    icon: "file-list-check-fill",
    tooltip: "Оплата по заявке проведена",
    interaction: "popover",
  },
];

export const ACTIVE_FINANCE_METRICS: MonitoringMetricItem[] = [
  {
    id: "ACTIVE_INVOICE_TOTAL",
    value: 0,
    label: "Общая сумма счетов, ₽",
    percent: 0,
    tone: "violet",
    icon: "payment-add",
    tooltip: "Сумма счетов по активным заявкам",
    interaction: "popover",
  },
  {
    id: "ACTIVE_PAYMENT_TOTAL",
    value: 0,
    label: "Поступило оплат, ₽",
    percent: 0,
    tone: "success",
    icon: "payment-check",
    tooltip: "Оплаты по активным заявкам",
    interaction: "popover",
  },
  {
    id: "ACTIVE_DEBT_TOTAL",
    value: 0,
    label: "Остался долг, ₽",
    percent: 0,
    tone: "error",
    icon: "payment-error",
    tooltip: "Разница между счетами и оплатами по активным",
    interaction: "popover",
  },
];

export const RAILWAY_SHARES: RailwayShareItem[] = [
  {
    id: getRequestsRailwayEnum.VOSTOCHNO_SIBIRSKAYA,
    name: "Восточно-Сибирская",
    count: 0,
    percent: 0,
  },
  { id: getRequestsRailwayEnum.GORKOVSKAYA, name: "Горьковская", count: 0, percent: 9.5 },
  { id: getRequestsRailwayEnum.DALNEVOSTOCHNAYA, name: "Дальневосточная", count: 0, percent: 4.1 },
  { id: getRequestsRailwayEnum.ZABAYKALSKAYA, name: "Забайкальская", count: 0, percent: 2.7 },
  {
    id: getRequestsRailwayEnum.ZAPADNO_SIBIRSKAYA,
    name: "Западно-Сибирская",
    count: 0,
    percent: 0,
  },
  { id: getRequestsRailwayEnum.KALININGRADSKAYA, name: "Калининградская", count: 0, percent: 2.7 },
  { id: getRequestsRailwayEnum.KRASNOYARSKAYA, name: "Красноярская", count: 0, percent: 2.7 },
  { id: getRequestsRailwayEnum.KUYBYSHEVSKAYA, name: "Куйбышевская", count: 0, percent: 2.7 },
  { id: getRequestsRailwayEnum.MOSKOVSKAYA, name: "Московская", count: 0, percent: 13.5 },
  { id: getRequestsRailwayEnum.OKTYABRSKAYA, name: "Октябрьская", count: 0, percent: 15.5 },
  { id: getRequestsRailwayEnum.PRIVOLZHSKAYA, name: "Приволжская", count: 0, percent: 4.7 },
  { id: getRequestsRailwayEnum.SVERDLOVSKAYA, name: "Свердловская", count: 0, percent: 8.1 },
  { id: getRequestsRailwayEnum.SEVERNAYA, name: "Северная", count: 0, percent: 10.8 },
  {
    id: getRequestsRailwayEnum.SEVERO_KAVKAZSKAYA,
    name: "Северо-Кавказская",
    count: 0,
    percent: 0,
  },
  { id: getRequestsRailwayEnum.YUGO_VOSTOCHNAYA, name: "Юго-Восточная", count: 0, percent: 3.4 },
  { id: getRequestsRailwayEnum.YUZHNO_URALSKAYA, name: "Южно-Уральская", count: 0, percent: 6.8 },
];

export const formatMonitoringNumber = (value: number): string => value.toLocaleString("ru-RU");

export const formatMonitoringPercent = (value: number): string => {
  if (value === 100) {
    return "100 %";
  }

  return `${value.toLocaleString("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

export const formatMonitoringDeadlineLabel = (remainingDays: number): string =>
  remainingDays < 0 ? `Просрочено на ${Math.abs(remainingDays)} дн.` : `Осталось ${remainingDays} дн.`;

export const formatMonitoringAmount = (value: number): string => `${formatMonitoringNumber(value)} ₽`;

export const splitRailwayShares = (items: RailwayShareItem[]): [RailwayShareItem[], RailwayShareItem[]] => {
  const middle = Math.ceil(items.length / 2);

  return [items.slice(0, middle), items.slice(middle)];
};

export const filterRailwayShares = (items: RailwayShareItem[], railwayId: string): RailwayShareItem[] => {
  const railways = getMonitoringRailwayQuery(railwayId);
  return railways ? items.filter((item) => railways.includes(item.id)) : items;
};

export const getMonitoringRailwayQuery = (railwayId?: string): GetRequestsRailwayEnumKey[] | undefined => {
  const railways = [...new Set(railwayId?.split(",").map((id) => id.trim()))].filter((id): id is GetRequestsRailwayEnumKey =>
    Object.values(getRequestsRailwayEnum).includes(id as GetRequestsRailwayEnumKey),
  );
  return railways.length > 0 && railways.length < Object.keys(getRequestsRailwayEnum).length ? railways : undefined;
};

export const getMonitoringRailwayLabel = (railwayId: string): string => {
  const railways = getMonitoringRailwayQuery(railwayId);
  if (!railways) return "Все дороги";
  return railways.length === 1 ? RAILWAY_SHARES.find((road) => road.id === railways[0])!.name : `Выбрано: ${railways.length}`;
};

export const getMonitoringMetrics = (metrics: MonitoringMetricItem[], railwayId: string, data: MonitoringResponse): MonitoringMetricItem[] =>
  metrics.map((metric) => {
    const indicator = data.indicators?.[metric.id];
    const isAmount = metric.id.endsWith("_TOTAL") && metric.id !== "TOTAL";
    return {
      ...metric,
      railwayId: railwayId === ALL_RAILWAYS_VALUE ? undefined : railwayId,
      value: (isAmount ? indicator?.amount : indicator?.requestCount) ?? 0,
      requestCount: indicator?.requestCount ?? 0,
      percent: indicator?.percent ?? 0,
    };
  });

export const getMonitoringRailwayShares = (data: MonitoringResponse, scope: RailwayScope): RailwayShareItem[] =>
  (data.roads ?? []).flatMap((road) =>
    road.railway
      ? [
          {
            id: road.railway,
            name: road.name ?? RAILWAY_SHARES.find((item) => item.id === road.railway)?.name ?? road.railway,
            count: (scope === "active" ? road.active : road.total) ?? 0,
            percent: (scope === "active" ? road.activePercent : road.totalPercent) ?? 0,
          },
        ]
      : [],
  );

const DEADLINE_STATES: Partial<Record<GetRequestsMonitoringMetricKey, MonitoringDeadlineState>> = {
  ACTIVE_PLANNED: "PLANNED",
  ACTIVE_APPROACHING: "DEADLINE_APPROACHING",
  ACTIVE_AT_RISK: "RISK_OF_VIOLATION",
  ACTIVE_OVERDUE: "OVERDUE",
};

export const mapMonitoringApplication = (
  item: MonitoringRequestItem,
  metric: GetRequestsMonitoringMetricKey,
): MonitoringPopoverApplication | undefined => {
  if (!item.id) return undefined;
  const amount =
    metric === "ACTIVE_PAYMENT_TOTAL"
      ? item.paymentAmount
      : metric === "ACTIVE_DEBT_TOTAL"
        ? item.debtAmount
        : metric === "ACTIVE_INVOICE_TOTAL" || metric === "ACTIVE_AWAITING_PAYMENT" || metric === "ACTIVE_PAID"
          ? item.invoiceAmount
          : undefined;
  return {
    id: item.id,
    number: item.number ?? 0,
    title: item.title ?? "",
    railwayName: RAILWAY_SHARES.find((road) => road.id === item.railway)?.name ?? item.railway ?? "",
    remainingDays: item.remainingDays ?? undefined,
    deadlineState: DEADLINE_STATES[metric],
    amount: amount ?? undefined,
  };
};
